import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { TIMELINE } from "@/data/content";

export const TimelineCarousel = () => {
  const trackRef = useRef(null);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-timeline-card]");
    const step = card ? card.offsetWidth + 24 : 340;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div data-testid="timeline">
      {/* Arrow navigation */}
      <div className="flex justify-end gap-3 mb-8">
        <button
          type="button"
          onClick={() => scrollByCards(-1)}
          aria-label="Previous milestones"
          data-testid="timeline-prev"
          className="h-11 w-11 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-foreground hover:text-background transition-colors duration-300"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => scrollByCards(1)}
          aria-label="Next milestones"
          data-testid="timeline-next"
          className="h-11 w-11 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-foreground hover:text-background transition-colors duration-300"
        >
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* Horizontal slider track */}
      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-testid="timeline-track"
      >
        {TIMELINE.map((m, i) => (
          <motion.div
            key={i}
            data-timeline-card
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="relative snap-start shrink-0 w-[260px] md:w-[300px] border-t border-border pr-6"
            data-testid={`timeline-item-${i}`}
          >
            <span className="absolute left-0 top-0 h-3 w-3 rounded-full bg-[hsl(var(--signal))] -translate-y-[6px]" />
            <p className="font-mono-tech text-sm text-[hsl(var(--signal))] pt-6 mb-4">{m.year}</p>
            <h3 className="font-display font-medium text-xl lg:text-2xl tracking-tight leading-tight mb-3">
              {m.title}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{m.text}</p>
          </motion.div>
        ))}

        {/* Continuation indicator — end of the journey */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative snap-start shrink-0 w-[280px] md:w-[320px] border-t border-dashed border-[hsl(var(--signal))] flex flex-col justify-start pr-6"
          data-testid="timeline-continuation"
        >
          <span className="absolute left-0 top-0 h-3 w-3 rounded-full border-2 border-dashed border-[hsl(var(--signal))] -translate-y-[6px]" />
          <div className="pt-6 mb-4">
            <svg
              width="140"
              height="72"
              viewBox="0 0 140 72"
              fill="none"
              className="text-[hsl(var(--signal))]"
              aria-hidden="true"
            >
              {/* Curved dotted trajectory rising up-right (~30deg) */}
              <path
                d="M6 62 C 46 60, 84 52, 120 20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="1 7"
                fill="none"
              />
              {/* Arrowhead at the tip, angled ~30deg upward */}
              <path
                d="M120 20 L106 21 M120 20 L112 33"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
          <p className="font-display italic text-lg md:text-xl tracking-tight leading-snug">
            Continuing to scale global exports and engineering excellence.
          </p>
        </motion.div>
      </div>
    </div>
  );
};
